import {Component, ElementRef, ViewChild} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {LoaderService} from 'ngx-satoris';
import {scaleLinear} from 'd3-scale';
import {line, curveCatmullRom} from 'd3-shape';
import {select, Selection} from 'd3-selection';

interface Packet {
  button_status: number;
  x: number;
  y: number;
  press: number;
}

interface DeviceInfo {
  xAxisRange: {
    axMin: number;
    axMax: number;
    axUnits: number;
    axResolution: number;
  },
  yAxisRange: {
    axMin: number;
    axMax: number;
    axUnits: number;
    axResolution: number;
  },
  maxPressure: number,
  width?: number,
  height?: number
}

@Component({
  selector: 'app-signature-modal',
  templateUrl: './signature-modal.component.html',
  styleUrl: './signature-modal.component.scss'
})
export class SignatureModalComponent {
  @ViewChild('signSvg') signSvg!: ElementRef<SVGElement>;
  private deviceInfo: DeviceInfo;
  private isCapturingSignature = false;
  private points: [number, number][][] = [[]];
  private xScale;
  private yScale;
  private draw;
  private d3Sign?: Selection<SVGElement, unknown, null, undefined> = undefined;
  private paths: any[] = [];

  constructor(public dialogRef: MatDialogRef<SignatureModalComponent>, private loader: LoaderService) {
    this.deviceInfo = electron.addon.viewSignGetDeviceInfo();
    this.deviceInfo.width = this.deviceInfo.xAxisRange.axMax - this.deviceInfo.xAxisRange.axMin;
    this.deviceInfo.height = this.deviceInfo.yAxisRange.axMax - this.deviceInfo.yAxisRange.axMin;
    this.xScale = scaleLinear()
      .domain([this.deviceInfo.xAxisRange.axMin, this.deviceInfo.xAxisRange.axMax])
      .range([this.deviceInfo.xAxisRange.axMin, this.deviceInfo.xAxisRange.axMax]);
    this.yScale = scaleLinear()
      .domain([this.deviceInfo.yAxisRange.axMin, this.deviceInfo.yAxisRange.axMax])
      .range([this.deviceInfo.yAxisRange.axMin, this.deviceInfo.yAxisRange.axMax]);
    this.draw = line()
      .x((d) => this.xScale(d[0]))
      .y((d) => this.yScale(d[1]))
      .curve(curveCatmullRom);
  }

  ngAfterViewInit(): void {
    const svg = this.signSvg.nativeElement;
    svg.setAttribute('width', `${this.deviceInfo.width}`);
    svg.setAttribute('height', `${this.deviceInfo.height}`);
    svg.setAttribute('viewBox', `0 0 ${this.deviceInfo.width} ${this.deviceInfo.height}`);
    this.isCapturingSignature = true;
    try {
      electron.addon.viewSignCloseDevice();
    } catch (_) {
      // Do nothing
    }
    try {
      electron.addon.viewSignOpenDevice();
      setTimeout(this.captureSignature.bind(this));
    } catch (error) {
      this.loader.loading(true, {type: 'error', message: error.message});
      electron.addon.viewSignCloseDevice();
    }
    electron.step('sign', true, [`${this.deviceInfo.width}`, `${this.deviceInfo.height}`]);
    requestAnimationFrame(this.renderSign.bind(this));
  }

  onResetClick(): void {
    this.points = [[]];
    this.paths = [];
    if(this.d3Sign) {
      this.d3Sign.selectAll('path').remove();
    }
  }
  onCancelClick(): void {
    this.isCapturingSignature = false;
    electron.step('default', false, []);
    this.dialogRef.close();
  }
  onValidateClick(): void {
    this.isCapturingSignature = false;
    electron.step('default', false, []);
    this.dialogRef.close(this.signatureToImage());
  }

  resetPressed({x, y}: Packet): boolean {
    if(this.deviceInfo.width === undefined || this.deviceInfo.height === undefined) {
      return false;
    }
    if(x < this.deviceInfo.width * 0.01 || x > this.deviceInfo.width * 0.16) {
      return false;
    }
    if(y < this.deviceInfo.width * 0.01 || y > this.deviceInfo.width * 0.06) {
      return false;
    }
    return true;
  }

  validatePressed({x, y}: Packet): boolean {
    if(this.deviceInfo.width === undefined || this.deviceInfo.height === undefined) {
      return false;
    }
    if(x < this.deviceInfo.width * 0.17 || x > this.deviceInfo.width * 0.32) {
      return false;
    }
    if(y < this.deviceInfo.width * 0.01 || y > this.deviceInfo.width * 0.06) {
      return false;
    }
    return true;
  }

  captureSignature() {
    if(!this.isCapturingSignature) {
      electron.addon.viewSignCloseDevice();
      return;
    }
    try {
      for(const packet of electron.addon.viewSignGetPackets()) {
        if(packet.press > this.deviceInfo.maxPressure / 10) {
          this.points[this.points.length - 1].push([packet.x, packet.y]);
        } else {
          if(this.resetPressed(packet)) {
            this.onResetClick();
          } else if(this.validatePressed(packet)) {
            this.onValidateClick();
          } else if(this.points[this.points.length - 1].length > 0) {
            this.points.push([]);
          }
        }
      }
      setTimeout(this.captureSignature.bind(this));
    } catch (error) {
      this.loader.loading(true, {type: 'error', message: error.message});
    }
  }

  private signatureToImage() {
    const svg = this.signSvg.nativeElement.cloneNode(true) as SVGSVGElement;
    const bbox = (this.signSvg.nativeElement as SVGSVGElement).getBBox();
    svg.setAttribute('viewBox',
      `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
    return svg.outerHTML; // TODO: transform to JPG or PNG?
  }

  private renderSign() {

    if(!this.deviceInfo) {
      this.isCapturingSignature = false;
      return;
    }

    if(this.d3Sign === undefined) {
      this.d3Sign = select(this.signSvg.nativeElement);
    }

    for(let offset = 0; offset < this.points.length; ++offset) {
      if(this.paths.length === offset) {
        this.paths.push(this.d3Sign.append('path')
          .attr('fill', 'none')
          .attr('stroke', 'black')
          .attr('stroke-width', 20));
      }
      const path = this.paths[offset];
      path.attr('d', this.draw(this.points[offset]));
    }
    electron.ext.signature.setInnerHTML(this.signSvg.nativeElement.innerHTML);

    if(this.isCapturingSignature) {
      requestAnimationFrame(this.renderSign.bind(this));
    }
  }
}
